import type { GlobalTourConfig } from '@components/tours/types';
import { WALLBOARD_TOUR_IDS } from '@components/tours/config/wallboard/constants';
import { OPERATOR_PANEL_TOUR_IDS } from '@components/tours/config/operatorPanel/constants';
import { WELCOME_TOUR_ID, WELCOME_SEGMENT_IDS, DEMO_WELCOME_MACHINE_ID, WELCOME_TOUR_IDS } from './constants';
import {
  injectMachinesBatchDemo,
  cleanupMachinesBatchDemo,
  injectPerformanceWelcomeDemo,
  cleanupPerformanceWelcomeDemo,
  injectWallboardWelcomeDemo,
  cleanupWallboardWelcomeDemo,
  injectOperatorPanelDemo,
  cleanupOperatorPanelDemo,
  cleanupOperatorPanelScheduledDemo,
  saveOriginalIsWithERP,
} from './demoData';
import { isTourActive, registerDemoOperationsSearch } from '@components/tours/helpers/demoDataRegistry';
import { setIsWithERP } from '@reducers/membership';
import { setStatStorage } from '@reducers/search';
import { createDemoScheduledOperation } from './demoData/fixtures';
import storeModule from '@store';

// ─── Inline SVG icons for Step 1 descriptions ─────────────────────────────────

// Batch linked to single job (blue circle + right arrow)
const linkedIcon =
  '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM3.11857 7.30655C2.72949 7.30655 2.42 7.60606 2.42 7.98485C2.42 8.37245 2.72949 8.68076 3.11857 8.68076H9.77785V9.86118C9.77785 10.469 10.3703 10.6892 10.8567 10.3192L13.3238 8.46934C13.6509 8.22269 13.6421 7.77343 13.3238 7.53558L10.8567 5.67687C10.388 5.32451 9.77785 5.5095 9.77785 6.13494V7.30655H3.11857Z" fill="#2DA0FC"/></svg>';

// Batch linked to multiple jobs (blue circle + multi-arrow)
const multiLinkIcon =
  '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><rect width="16" height="16" rx="8" fill="#2DA0FC"/><path d="M3 7.99353C3 7.61853 3.20937 7.43103 3.62813 7.43103H6.13594C6.40469 7.43103 6.69063 7.36072 6.99375 7.22009C7.29688 7.07947 7.59531 6.89666 7.88906 6.67166C8.18594 6.44978 8.45469 6.21072 8.69531 5.95447C8.93906 5.69822 9.13594 5.45291 9.28594 5.21853L9.53438 4.83416C9.65 4.65291 9.8125 4.56228 10.0219 4.56228C10.1688 4.56228 10.2969 4.60916 10.4063 4.70291C10.5156 4.79666 10.5703 4.93259 10.5703 5.11072C10.5703 5.27009 10.5219 5.42322 10.425 5.57009L10.2656 5.81384C10.1219 6.02947 9.94531 6.25134 9.73594 6.47947C9.52969 6.70759 9.30781 6.92478 9.07031 7.13103C8.83281 7.33728 8.59688 7.51541 8.3625 7.66541C8.12813 7.81541 7.9125 7.92166 7.71563 7.98416V8.00291C7.9125 8.06541 8.12813 8.17166 8.3625 8.32166C8.59688 8.47478 8.83281 8.65291 9.07031 8.85603C9.30781 9.06228 9.52969 9.27947 9.73594 9.50759C9.94531 9.73572 10.1219 9.95916 10.2656 10.1779L10.425 10.4217C10.5219 10.5685 10.5703 10.7201 10.5703 10.8763C10.5703 11.0545 10.5172 11.1904 10.4109 11.2842C10.3047 11.381 10.175 11.4295 10.0219 11.4295C9.8125 11.4295 9.65 11.3373 9.53438 11.1529L9.28594 10.7685C9.13594 10.5342 8.93906 10.2873 8.69531 10.0279C8.45469 9.77166 8.18594 9.53259 7.88906 9.31072C7.59531 9.08884 7.29688 8.90759 6.99375 8.76697C6.69063 8.62947 6.40469 8.56072 6.13594 8.56072H3.62813C3.20937 8.56072 3 8.37166 3 7.99353ZM8.70938 4.58103C8.55 4.45603 8.49375 4.32322 8.54063 4.18259C8.5875 4.04509 8.7125 3.94666 8.91563 3.88728L10.9453 3.30134C11.1078 3.25134 11.2438 3.26853 11.3531 3.35291C11.4656 3.44041 11.5188 3.56853 11.5125 3.73728L11.4375 5.85134C11.4312 6.06384 11.3656 6.20759 11.2406 6.28259C11.1156 6.36072 10.9734 6.33884 10.8141 6.21697L8.70938 4.58103ZM8.72344 11.331L10.8797 9.76541C11.0391 9.64666 11.1797 9.62791 11.3016 9.70916C11.4266 9.79041 11.4891 9.93728 11.4891 10.1498L11.4938 12.2638C11.4938 12.4357 11.4359 12.5623 11.3203 12.6435C11.2078 12.7248 11.0719 12.7388 10.9125 12.6857L8.90156 12.0295C8.70156 11.967 8.57969 11.8638 8.53594 11.7201C8.49531 11.5763 8.55781 11.4467 8.72344 11.331Z" fill="white"/></svg>';

// Batch not linked to Job (yellow circle + broken arrow)
const notLinkedIcon =
  '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8Z" fill="#FFCC00"/><path d="M3.12356 7.30655C2.73448 7.30655 2.42499 7.60606 2.42499 7.98485C2.42499 8.37245 2.73448 8.68076 3.12356 8.68076H6.8017L5.42749 7.30655H3.12356Z" fill="black"/><path d="M7.31206 7.30655L8.68628 8.68076H9.78284V9.86118C9.78284 10.469 10.3753 10.6892 10.8616 10.3192L13.3287 8.46934C13.6559 8.22269 13.6471 7.77343 13.3287 7.53558L10.8616 5.67687C10.393 5.32451 9.78284 5.5095 9.78284 6.13494V7.30655H7.31206Z" fill="black"/></svg>';

// Unknown NC File (yellow circle + exclamation mark)
const unknownNcFileIcon =
  '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><circle cx="6" cy="6" r="6" fill="#FFCC00"/><path d="M5.58984 7.15625H6.40039L6.4834 2.44922H5.50684L5.58984 7.15625ZM6 9.58789C6.39551 9.58789 6.67383 9.30469 6.67383 8.90918C6.67383 8.50879 6.39551 8.22559 6 8.22559C5.60449 8.22559 5.32129 8.50879 5.32129 8.90918C5.32129 9.30469 5.60449 9.58789 6 9.58789Z" fill="black"/></svg>';

// Ignored NC File (gray muted bell with slash)
const ignoredNcFileIcon =
  '<svg viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path d="M13.3618 10.3555L11.4637 8.85156C11.4637 8.80859 11.4848 8.78711 11.4848 8.74414C11.4848 8.59375 11.4215 8.42188 11.295 8.29297C10.8942 7.8418 10.1139 7.17578 10.1139 4.96289C10.1139 3.30859 8.97502 1.97656 7.41434 1.63281V1.18164C7.41434 0.816406 7.11908 0.494141 6.76055 0.494141C6.38092 0.494141 6.08566 0.816406 6.08566 1.18164V1.63281C5.22095 1.82617 4.50389 2.29883 4.01881 2.98633L0.960719 0.580078C0.813087 0.451172 0.602184 0.494141 0.496733 0.644531L0.0749276 1.18164C-0.0516141 1.33203 -0.00943354 1.54688 0.138198 1.6543L12.5393 11.4297C12.6869 11.5586 12.8978 11.5156 13.0033 11.3652L13.4251 10.8281C13.5516 10.6777 13.5094 10.4629 13.3618 10.3555ZM3.32283 5.9082C3.13302 7.36914 2.54249 7.90625 2.20505 8.29297C2.0785 8.42188 2.01523 8.59375 2.03632 8.74414C2.03632 9.10938 2.28941 9.43164 2.71121 9.43164H7.81506L3.32283 5.9082ZM6.76055 11.4941C7.4987 11.4941 8.08923 10.8926 8.08923 10.1191H5.41077C5.41077 10.8926 6.0013 11.4941 6.76055 11.4941Z" fill="#8A8A94"/></svg>';

// Change NC File (orange hexagonal cycle arrows)
const changeNcFileIcon =
  '<svg viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.77902 6.28856L0.798336 5.23219C0.651019 5.15314 0.544125 5.06152 0.477653 4.95732C0.411181 4.85132 0.377945 4.7076 0.377945 4.52615V2.20591C0.377945 2.05141 0.409384 1.92026 0.472263 1.81247C0.535142 1.70468 0.638443 1.61395 0.782167 1.5403L2.6335 0.545911C2.81136 0.448897 3 0.400391 3.19941 0.400391C3.39883 0.400391 3.58747 0.448897 3.76532 0.545911L5.61936 1.5403C5.76128 1.61395 5.86368 1.70468 5.92656 1.81247C5.98944 1.92026 6.02088 2.05141 6.02088 2.20591V4.52615C6.02088 4.7076 5.98765 4.85132 5.92117 4.95732C5.8547 5.06152 5.74691 5.15314 5.5978 5.23219L3.6198 6.28856C3.33954 6.43767 3.05928 6.43767 2.77902 6.28856Z" fill="#FF9500"/><path d="M10.0064 6.77979C10.0796 6.87646 10.169 6.9248 10.2744 6.9248C10.3799 6.92188 10.4693 6.87354 10.5425 6.77979L11.5357 5.4834C11.647 5.33691 11.6748 5.19482 11.6192 5.05713C11.5664 4.9165 11.4434 4.84619 11.25 4.84619H10.7183V4.55176C10.7183 4.09473 10.6158 3.68604 10.4107 3.32568C10.2056 2.96533 9.92142 2.68115 9.55814 2.47314C9.19779 2.26514 8.78324 2.16113 8.31449 2.16113H7.50589C7.37992 2.16113 7.27298 2.20508 7.18509 2.29297C7.0972 2.38086 7.05326 2.48779 7.05326 2.61377C7.05326 2.73682 7.0972 2.84375 7.18509 2.93457C7.27298 3.02246 7.37992 3.06641 7.50589 3.06641H8.31449C8.61332 3.06641 8.87845 3.12939 9.1099 3.25537C9.34427 3.38135 9.52738 3.5542 9.65921 3.77393C9.79105 3.99365 9.85697 4.24561 9.85697 4.52979V4.84619H9.29007C9.1055 4.84619 8.98685 4.9165 8.93412 5.05713C8.88138 5.19482 8.91068 5.33691 9.02201 5.4834L10.0064 6.77979Z" fill="#FF9500"/><path d="M3.68265 11.7939H4.49564C4.62162 11.7939 4.72855 11.7485 4.81644 11.6577C4.90433 11.5698 4.94828 11.4644 4.94828 11.3413C4.94828 11.2183 4.90433 11.1113 4.81644 11.0205C4.72855 10.9326 4.62162 10.8887 4.49564 10.8887H3.68265C3.38675 10.8887 3.12162 10.8257 2.88724 10.6997C2.6558 10.5737 2.47269 10.4009 2.33793 10.1812C2.20609 9.96143 2.14017 9.70801 2.14017 9.4209V9.10889H2.70707C2.89457 9.10889 3.01468 9.03857 3.06742 8.89795C3.12015 8.75732 3.09086 8.61377 2.97953 8.46729L1.99515 7.17529C1.91898 7.07861 1.82816 7.03174 1.72269 7.03467C1.62015 7.03467 1.53226 7.08154 1.45902 7.17529L0.465855 8.46729C0.354527 8.6167 0.32523 8.76172 0.377965 8.90234C0.430699 9.04004 0.555211 9.10889 0.7515 9.10889H1.28324V9.40332C1.28324 9.85742 1.38578 10.2646 1.59086 10.625C1.79593 10.9854 2.07865 11.2695 2.439 11.4775C2.79935 11.6885 3.2139 11.7939 3.68265 11.7939Z" fill="#FF9500"/><path d="M6.40081 12.4322L8.3815 13.4886C8.66176 13.6377 8.94202 13.6377 9.22228 13.4886L11.2003 12.4322C11.3494 12.3531 11.4572 12.2615 11.5237 12.1573C11.5901 12.0513 11.6234 11.9076 11.6234 11.7261V9.40591C11.6234 9.25141 11.5919 9.12026 11.529 9.01247C11.4662 8.90468 11.3638 8.81395 11.2218 8.7403L9.3678 7.74591C9.18994 7.6489 9.00131 7.60039 8.80189 7.60039C8.60248 7.60039 8.41384 7.6489 8.23598 7.74591L6.38464 8.7403C6.24092 8.81395 6.13762 8.90468 6.07474 9.01247C6.01186 9.12026 5.98042 9.25141 5.98042 9.40591V11.7261C5.98042 11.9076 6.01366 12.0513 6.08013 12.1573C6.1466 12.2615 6.2535 12.3531 6.40081 12.4322Z" fill="#FF9500"/></svg>';

// ─── Shared HTML fragments ─────────────────────────────────────────────────────

const iconRow = (icon: string, text: string) =>
  `<div style="display:flex;align-items:center;gap:6px;">${icon}<span>${text}</span></div>`;

const statusDot = (color: string) =>
  `<span style="display:inline-block;width:10px;min-width:10px;height:10px;background:${color};border-radius:50%;flex-shrink:0;"></span>`;

const statusBar = (color: string) =>
  `<span style="display:inline-block;width:24px;height:8px;background:${color};border-radius:2px;"></span>`;

const batchBadge = (color: string, label: string) =>
  `<span style="display:inline-block;width:auto;padding:1px 8px;background:${color};border-radius:8px;color:white;font-size:11px;line-height:16px;">${label}</span>`;

// ─── Machine status HTML (reused across steps 3, 10, 20) ───────────────────────
// Order: Active, Change, Setup, Not Productive

const machineStatusDots =
  '<div style="display:flex;flex-direction:column;gap:6px;">' +
  iconRow(statusDot('#65C366'), '<b>Active</b> \u2014 machine is currently producing') +
  iconRow(statusDot('#FF9500'), '<b>Change</b> \u2014 switching from one workpiece to another in the same batch') +
  iconRow(statusDot('#E040FB'), '<b>Setup</b> \u2014 machine is being prepared for production') +
  iconRow(statusDot('#8A8A94'), '<b>Not Productive</b> \u2014 machine is not actively producing') +
  '</div>';

const machineStatusBars =
  '<div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">' +
  `<div style="display:flex;align-items:center;gap:8px;">${statusBar('#65C366')}<span><b>Active</b> \u2014 producing</span></div>` +
  `<div style="display:flex;align-items:center;gap:8px;">${statusBar('#FF9500')}<span><b>Change</b> \u2014 workpiece changeover</span></div>` +
  `<div style="display:flex;align-items:center;gap:8px;">${statusBar('#E040FB')}<span><b>Setup</b> \u2014 being prepared for production</span></div>` +
  `<div style="display:flex;align-items:center;gap:8px;">${statusBar('#8A8A94')}<span><b>Not Productive</b> \u2014 not actively producing</span></div>` +
  '</div>';

// ─── OEE 5-level HTML (reused in steps 4) ──────────────────────────────────────

const oeeLevels =
  '<div style="display:flex;flex-direction:column;gap:6px;">' +
  iconRow(statusDot('#FF3B30'), '<b>Poor</b> \u2014 below 40%') +
  iconRow(statusDot('#FF9500'), '<b>Fair</b> \u2014 40% to 60%') +
  iconRow(statusDot('#34C759'), '<b>Good</b> \u2014 60% to 75%') +
  iconRow(statusDot('#007AFF'), '<b>Excellent</b> \u2014 75% to 85%') +
  iconRow(statusDot('#AF52DE'), '<b>World Class</b> \u2014 85% and above') +
  '</div>';

// ─── Step 1 descriptions (ERP / non-ERP) ───────────────────────────────────────

const linkStatusDescriptionERP =
  'This section shows the link status of the current batch. The icon indicates whether the batch is linked to a job:<br/><br/>' +
  '<div style="display:flex;flex-direction:column;gap:6px;">' +
  iconRow(linkedIcon, '<b>Linked to Job</b> \u2014 the batch is linked to one production job') +
  iconRow(multiLinkIcon, '<b>Linked to multiple Jobs</b> \u2014 the batch is linked to more than one job') +
  iconRow(notLinkedIcon, '<b>Not Linked to Job</b> \u2014 the NC file type is known, but the batch is not yet linked to a job') +
  iconRow(unknownNcFileIcon, '<b>Unknown NC File</b> \u2014 is this NC file for production or should it be ignored?') +
  iconRow(ignoredNcFileIcon, '<b>Ignored NC File</b> \u2014 production time is excluded from reporting') +
  iconRow(changeNcFileIcon, '<b>Change NC File</b> \u2014 this NC file runs between parts. Production time counts as changeover') +
  '</div>';

const linkStatusDescriptionNonERP =
  'This section shows the NC file that is currently running on the machine and its type:<br/><br/>' +
  '<div style="display:flex;flex-direction:column;gap:6px;">' +
  iconRow(linkedIcon, '<b>Linked</b> \u2014 this NC file is used for production') +
  iconRow(unknownNcFileIcon, '<b>Unknown NC File</b> \u2014 is this NC file for production or should it be ignored?') +
  iconRow(ignoredNcFileIcon, '<b>Ignored NC File</b> \u2014 production time is excluded from reporting') +
  iconRow(changeNcFileIcon, '<b>Change NC File</b> \u2014 this NC file runs between parts. Production time counts as changeover') +
  '</div>';

// ─── Route param helpers ────────────────────────────────────────────────────────

/**
 * Get route params for Operator Panel segment.
 * Fetches the first available machine ID from Redux store.
 */
const getOperatorPanelParams = (): Record<string, any> => {
  if (isTourActive()) {
    return { id: DEMO_WELCOME_MACHINE_ID, hideNcFilePopup: true };
  }

  const state = storeModule.store.getState();
  const machines = state.machines?.machines || [];
  const firstMachine = machines[0];

  if (firstMachine?.machineId) {
    return { id: firstMachine.machineId, hideNcFilePopup: true };
  }

  // Fallback: check selectedItem
  const selectedMachine = state.machines?.selectedItem;
  if (selectedMachine?.machineId) {
    return { id: selectedMachine.machineId, hideNcFilePopup: true };
  }

  // No machine available - return empty (segment will skip on timeout)
  console.warn('No machine available for Operator Panel tour segment');
  return {};
};

/**
 * Get route params for Operator Panel > Scheduled Jobs segment.
 * OperatorPanelScheduledScreen is a root-level screen (sibling to OperatorPanel),
 * so we navigate directly to it — matching how useManageHomePageButtonsPress does it.
 *
 * This segment shares `inject: injectOperatorPanelDemo` with the OP segment so the
 * conductor skips cleanup between them (avoids intermediate empty state that triggers
 * OperatorPanel's useFocusEffect → navigateBack → closes the modal).
 * Scheduled-specific data is injected here, right before navigation.
 */
const getOperatorPanelScheduledParams = (): Record<string, any> => {
  const { store } = storeModule;

  // Inject scheduled-specific demo data (OP base data already active from shared inject)
  registerDemoOperationsSearch({
    searchResult: [createDemoScheduledOperation()],
    totalResultCount: 1,
  });

  // Save original isWithERP before overwriting — cleanup restores it
  saveOriginalIsWithERP(store.getState().membership?.isWithERP);
  store.dispatch(setIsWithERP(true));
  store.dispatch(setStatStorage({ name: 'OPERATIONS_NOW', value: 1 }));
  store.dispatch(setStatStorage({ name: 'OPERATIONS_LATE', value: 0 }));
  store.dispatch(setStatStorage({ name: 'OPERATIONS_NEXT_WEEK', value: 0 }));
  store.dispatch(setStatStorage({ name: 'OPERATIONS_AFTER_NEXT_WEEK', value: 0 }));

  return { id: DEMO_WELCOME_MACHINE_ID };
};

/**
 * Get route params for Machines > Live Overview segment.
 * Navigates to the overview page (no nested sub-screen navigation).
 */
const getMachinesLiveOverviewParams = (): Record<string, any> => ({
  screen: 'MachinesLive',
  params: { id: DEMO_WELCOME_MACHINE_ID },
});

/**
 * Get route params for Machines > Performance > Active segment.
 * Uses nested React Navigation screen params to navigate directly
 * to the Performance > Active sub-screen.
 */
const getMachinesPerformanceParams = (): Record<string, any> => ({
  screen: 'MachinesPerformance',
  params: {
    screen: 'MachinesActive',
    params: { id: DEMO_WELCOME_MACHINE_ID, timePeriod: 'day' },
  },
});

// ─── Factory function ───────────────────────────────────────────────────────────

/**
 * Welcome Tour - Global multi-page tour for new users
 *
 * Segments follow the salesman demo flow:
 * 1. Machines > Live Overview (with demo data injection)
 * 2. Machines > Performance (with performance-specific demo data injection)
 * 3. Wallboard (with wallboard-specific demo data injection)
 * 4. Operator Panel
 * 5. Operator Panel > Scheduled Jobs (ERP only)
 *
 * @param isWithERP - Whether the customer has ERP integration enabled.
 *   Affects step content (steps 1, 14, 21, 23) and segment inclusion (segment 5, step 16).
 */
export const createWelcomeTour = (isWithERP: boolean): GlobalTourConfig => ({
  id: WELCOME_TOUR_ID,
  name: 'Welcome Tour',
  description: 'Get a quick overview of Smarfac\'s key features',
  segments: [
    // Segment 1: Machines > Live Overview (with demo data)
    {
      id: WELCOME_SEGMENT_IDS.MACHINES_LIVE_OVERVIEW,
      route: 'Authorized',
      routeParams: getMachinesLiveOverviewParams,
      displayName: 'Machines \u2014 Live',
      steps: [
        {
          tourId: WELCOME_TOUR_IDS.LIVE_TOP_BLOCK,
          popover: {
            title: 'Link Status',
            description: isWithERP ? linkStatusDescriptionERP : linkStatusDescriptionNonERP,
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.LIVE_BATCH_BLOCK,
          popover: {
            title: 'Current Batch',
            description:
              'This section shows the active production run: batch number, status, running timer, and a mini-timeline of recent events.<br/><br/>' +
              '<div style="display:flex;flex-direction:column;gap:6px;">' +
              `<div style="display:flex;align-items:center;gap:6px;">${batchBadge('#2DA0FC', 'Running')}<span>\u2014 batch is actively in production</span></div>` +
              `<div style="display:flex;align-items:center;gap:6px;">${batchBadge('#A2845E', 'Paused')}<span>\u2014 production is temporarily halted</span></div>` +
              `<div style="display:flex;align-items:center;gap:6px;">${batchBadge('#000000', 'Finished')}<span>\u2014 batch has been completed</span></div>` +
              '<div><b>Ready PCs</b> \u2014 finished pieces produced so far</div>' +
              '<div><b>Started / Expected End</b> \u2014 when the batch started and when it\'s expected to finish</div>' +
              '</div>',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.LIVE_ACTIVITY_BLOCK,
          popover: {
            title: 'Machine Activity',
            description:
              'This section shows the machine\'s current state with a real-time progress bar. These status colors are used throughout the app:<br/><br/>' +
              machineStatusDots,
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.LIVE_OEE_BLOCK,
          popover: {
            title: 'OEE Today',
            description:
              'OEE (Overall Equipment Effectiveness) measures how productively this machine is running today. The value is color-coded:<br/><br/>' +
              oeeLevels +
              '<br/>Tap to see the full breakdown: Availability, Performance, and Quality.',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.LIVE_TOOLS_BLOCK,
          popover: {
            title: 'Tools',
            description: 'This section shows how many tools are used in the current NC file. Tap to see the full list with tool numbers, names, and descriptions.',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.LIVE_NC_FILE_BLOCK,
          onHighlightStarted: (element) => {
            if (!element) return;
            const original = element.scrollIntoView.bind(element);
            element.scrollIntoView = () => {};
            setTimeout(() => { element.scrollIntoView = original; }, 200);
          },
          popover: {
            title: 'NC File Content',
            description: 'A preview of the G-code in this NC file. Tap <b>Full NC File</b> to view the complete program.',
          },
        },
      ],
      demoData: {
        inject: injectMachinesBatchDemo,
        cleanup: cleanupMachinesBatchDemo,
        injectDelay: 500,
      },
      waitForSelector: `[data-tour-id="${WELCOME_TOUR_IDS.LIVE_TOP_BLOCK}"]`,
      waitTimeout: 8000,
    },
    // Segment 2: Machines > Performance > Active (with performance demo data)
    {
      id: WELCOME_SEGMENT_IDS.MACHINES_PERFORMANCE,
      route: 'Authorized',
      routeParams: getMachinesPerformanceParams,
      displayName: 'Machines \u2014 Performance',
      steps: [
        {
          tourId: WELCOME_TOUR_IDS.PERFORMANCE_CHART,
          popover: {
            title: 'Performance Chart',
            description:
              'The <b>Performance</b> page shows machine activity over time. This chart displays the <b>Active</b> hours for the selected machine today \u2014 each bar represents one hour.',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.PERFORMANCE_TABS,
          popover: {
            title: 'Metric Types',
            description:
              'Switch between metric types to analyze different aspects of machine performance:<br/>' +
              '<div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">' +
              '<div><b>Utilization</b> \u2014 machine uptime vs available time</div>' +
              '<div><b>Active</b> \u2014 total producing time</div>' +
              '<div><b>Productive</b> \u2014 active + change + setup time</div>' +
              '<div><b>Paid</b> \u2014 time against customer billing</div>' +
              '</div>',
          },
        },
        // {
        //   tourId: WELCOME_TOUR_IDS.PERFORMANCE_BAR_CHART,
        //   popover: {
        //     title: 'Hourly Breakdown',
        //     description:
        //       'Each bar shows the hourly breakdown by status. Colors match the entire app:' +
        //       '<div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">' +
        //       '<div style="display:flex;align-items:center;gap:8px;">' +
        //       '<span style="display:inline-block;width:24px;height:8px;background:#65C366;border-radius:2px;"></span>' +
        //       '<span><b>Active</b> — producing</span>' +
        //       '</div>' +
        //       '<div style="display:flex;align-items:center;gap:8px;">' +
        //       '<span style="display:inline-block;width:24px;height:8px;background:#FF9500;border-radius:2px;"></span>' +
        //       '<span><b>Change</b> — changeover</span>' +
        //       '</div>' +
        //       '<div style="display:flex;align-items:center;gap:8px;">' +
        //       '<span style="display:inline-block;width:24px;height:8px;background:#E040FB;border-radius:2px;"></span>' +
        //       '<span><b>Setup</b> — preparing</span>' +
        //       '</div>' +
        //       '<div style="display:flex;align-items:center;gap:8px;">' +
        //       '<span style="display:inline-block;width:24px;height:8px;background:#8A8A94;border-radius:2px;"></span>' +
        //       '<span><b>Not Productive</b> — idle</span>' +
        //       '</div>' +
        //       '</div>',
        //   },
        // },
      ],
      demoData: {
        inject: injectPerformanceWelcomeDemo,
        cleanup: cleanupPerformanceWelcomeDemo,
        injectDelay: 500,
      },
      waitForSelector: `[data-tour-id="${WELCOME_TOUR_IDS.PERFORMANCE_CHART}"]`,
      waitTimeout: 8000,
    },
    // Segment 3: Wallboard (with demo data)
    {
      id: WELCOME_SEGMENT_IDS.WALLBOARD,
      route: 'Wallboard',
      displayName: 'Wallboard',
      steps: [
        {
          tourId: WALLBOARD_TOUR_IDS.MACHINE_TILE,
          popover: {
            title: 'Machine Tile',
            description: 'The <b>Wallboard</b> gives you a real-time overview of all your machines. Each tile shows the current status, part being produced, and key production metrics at a glance.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.TILE_HEADER,
          popover: {
            title: 'Status Header',
            description:
              'The colored header shows the machine\'s current status. Colors are consistent across the entire app:' +
              machineStatusBars,
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.PART_IMAGE,
          popover: {
            title: 'Part Drawing / NC File',
            description: 'This area shows what\'s currently being manufactured. When a <b>part image</b> is available, it appears here \u2014 helping operators visually confirm the correct part is loaded. If no image is uploaded, a preview of the <b>NC file content</b> (G-code) is shown instead.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.PRODUCTIVITY_TIMELINE,
          popover: {
            title: 'Productivity Time',
            description: '<b>Productivity Time</b> shows how long the machine has been in the current status. The colored bar fills up relative to targets: Scheduled, Paid, and Ideal times \u2014 so you can spot overruns instantly.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.BATCH_TIMELINE,
          popover: {
            title: 'Batch Timeline',
            description: 'The <b>Batch Timeline</b> shows the total batch duration with a colored bar representing status changes throughout the run \u2014 Setup, Active, and Change phases.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.PIECES_PROGRESS,
          popover: {
            title: 'Pieces Gauge',
            description: isWithERP
              ? 'The <b>Pieces</b> gauge shows completed parts vs. the target quantity. The circular arc fills as production advances \u2014 giving a quick visual of how close the batch is to completion.'
              : 'The <b>Pieces</b> gauge shows the number of completed parts in the current batch.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.OEE_GAUGE,
          popover: {
            title: 'OEE Today',
            description: '<b>OEE Today</b> shows the overall equipment effectiveness for the current day. The value is color-coded across five levels: <b>purple</b> for world class, <b>blue</b> for excellent, <b>green</b> for good, <b>orange</b> for fair, and <b>red</b> for poor.',
          },
        },
        // Step 16: SCHEDULE_GAUGE — ERP only
        ...(isWithERP
          ? [
              {
                tourId: WALLBOARD_TOUR_IDS.SCHEDULE_GAUGE,
                popover: {
                  title: 'Days Ahead / Behind',
                  description: 'The <b>Schedule</b> gauge shows the ERP schedule deviation. <b>Green</b> means the machine is ahead of schedule, <b>red</b> means it\'s behind \u2014 helping you spot delays at a glance.',
                },
              },
            ]
          : []),
        {
          tourId: WALLBOARD_TOUR_IDS.LEVEL_SELECTOR,
          popover: {
            title: 'Wallboard View',
            description: 'Switch between <b>Now</b> (live tiles), <b>24h</b> (last 24 hours history), or <b>Day</b> view to see different time perspectives of your production floor.',
          },
        },
        {
          tourId: WALLBOARD_TOUR_IDS.MACHINE_FILTER,
          popover: {
            title: 'Machine Filter',
            description: '<b>Filter</b> machines by department or group. Use this to focus on a specific production area when you have many machines.',
          },
        },
      ],
      demoData: {
        inject: injectWallboardWelcomeDemo,
        cleanup: cleanupWallboardWelcomeDemo,
        injectDelay: 500,
      },
      waitForSelector: `[data-tour-id="${WALLBOARD_TOUR_IDS.MACHINE_TILE}"]`,
      waitTimeout: 8000,
    },
    // Segment 4: Operator Panel (with demo data)
    {
      id: WELCOME_SEGMENT_IDS.OPERATOR_PANEL,
      route: 'OperatorPanel',
      routeParams: getOperatorPanelParams,
      displayName: 'Operator Panel',
      steps: [
        {
          tourId: OPERATOR_PANEL_TOUR_IDS.MAIN_CONTAINER,
          popover: {
            title: 'Operator Panel',
            description: 'The <b>Operator Panel</b> is the shop floor interface. Operators use this full-screen view to monitor production, log piece counts, and access job documents \u2014 all without leaving the machine.',
          },
        },
        {
          tourId: OPERATOR_PANEL_TOUR_IDS.HEADER,
          popover: {
            title: 'Machine & Status',
            description:
              'The header shows the current <b>machine name</b> and production <b>status</b>. The background color reflects the machine state:' +
              '<div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">' +
              iconRow(statusDot('#65C366'), '<b>Active</b> \u2014 machine is currently producing') +
              iconRow(statusDot('#FF9500'), '<b>Change</b> \u2014 switching from one workpiece to another') +
              iconRow(statusDot('#E040FB'), '<b>Setup</b> \u2014 machine is being prepared for production') +
              iconRow(statusDot('#8A8A94'), '<b>Not Productive</b> \u2014 machine is not actively producing') +
              '</div>' +
              '<br/>Tap the machine name to switch machines, or tap the operator badge to access settings.',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.OP_NC_FILE_INFO,
          popover: {
            title: isWithERP ? 'NC File & Job Info' : 'NC File Info',
            description: isWithERP
              ? 'This area shows the active <b>NC file</b> name and its <b>job linking status</b>. When linked to a job, it displays the operation number and piece target.'
              : 'This area shows the active <b>NC file</b> name and its type (Linked, Change, Ignored, or Unknown).',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.OP_COUNTER,
          popover: {
            title: 'Piece Counter',
            description: 'The central counter shows <b>Ready</b> pieces produced. Operators tap <b>+</b> or <b>\u2212</b> to manually correct the count. The number updates in real-time from the machine connection.',
          },
        },
        {
          tourId: WELCOME_TOUR_IDS.OP_BOTTOM_ACTIONS,
          popover: {
            title: 'Quick Actions',
            description: isWithERP
              ? '<b>Information</b> opens job documents, setup sheets, and NC files.<br/><b>Link To Job</b> connects the current batch to a production order.<br/><b>Scheduled</b> shows upcoming jobs for this machine.'
              : '<b>Information</b> opens setup sheets, NC files, and documents.<br/><b>Select NC File Type</b> lets you classify the current NC file (Linked, Change, Ignored).<br/><b>NC Files</b> shows all NC files on this machine.',
          },
        },
      ],
      demoData: {
        inject: injectOperatorPanelDemo,
        cleanup: cleanupOperatorPanelDemo,
        injectDelay: 500,
      },
      waitForSelector: `[data-tour-id="${OPERATOR_PANEL_TOUR_IDS.MAIN_CONTAINER}"]`,
      waitTimeout: 8000,
    },
    // Segment 5: Operator Panel > Scheduled Jobs (ERP only)
    ...(isWithERP
      ? [
          {
            id: WELCOME_SEGMENT_IDS.OPERATOR_PANEL_SCHEDULED,
            route: 'OperatorPanelScheduledScreen',
            routeParams: getOperatorPanelScheduledParams,
            displayName: 'Scheduled Jobs',
            steps: [
              {
                tourId: WELCOME_TOUR_IDS.OP_SCHEDULED_OVERVIEW,
                popover: {
                  title: 'Scheduled Jobs',
                  description: 'The <b>Scheduled Jobs</b> screen shows all upcoming production operations from your ERP system. Each job card shows the operation number, preferred machine, schedule date, and required quantities.',
                },
              },
              {
                tourId: WELCOME_TOUR_IDS.OP_SCHEDULED_JOB_TILE,
                popover: {
                  title: 'Job Card',
                  description: 'Each card represents a <b>production operation</b>. You can see the job number, assigned machine, start date, and NC file count. Tap a job to view its full details \u2014 documents, drawings, and linked NC files.',
                },
              },
            ],
            demoData: {
              inject: injectOperatorPanelDemo,
              cleanup: cleanupOperatorPanelScheduledDemo,
              injectDelay: 500,
            },
            waitForSelector: `[data-tour-id="${WELCOME_TOUR_IDS.OP_SCHEDULED_OVERVIEW}"]`,
            waitTimeout: 8000,
          },
        ]
      : []),
  ],
});
